import React, { useState,useEffect } from 'react';
import { X, AlertCircle, Download, Trash2,CheckCircle, Clock, Loader2, FileText, User, Database, Shield, Smartphone, ExternalLink, LogOut, Settings as SettingsIcon, Star, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import {useAuth} from '../hooks/useAuth'
import {
  dataDownloadService,
  DataDownloadRequest,
} from "../services/dataDownloadService";
// import Loading from '../components/sub-components/loading';
import ChangePasswordModal from './ChangePasswordModal';
import { Session } from '@supabase/supabase-js';


interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = function SubscriptionModal(props) {
  if (!props.isOpen) return null;
  const { t } = useTranslation();
  const { session, user, signOut } = useAuth();

  const [activeSection, setActiveSection] = useState<'general' | 'data' | 'legal' | 'security' | 'app'>('general');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Data Download State
  const [downloadRequest, setDownloadRequest] =
    useState<DataDownloadRequest | null>(null);
  const [isCheckingDownloadStatus, setIsCheckingDownloadStatus] =
    useState(false);
  const [isCreatingDownloadRequest, setIsCreatingDownloadRequest] =
    useState(false);
  const [isDownloadingData, setIsDownloadingData] = useState(false);
  const [dataDownloadError, setDataDownloadError] = useState<string | null>(
    null
  );
  const [dataDownloadSuccess, setDataDownloadSuccess] = useState<
    string | null
  >(null);

  // Delete Account State
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);


  const loadDownloadStatus = async () => {
    if (!user?.id) return;
    setIsCheckingDownloadStatus(true);
    setDataDownloadError(null);
    try {
      const raw = await dataDownloadService.checkDownloadStatus(user.id, session as Session);
      const normalizedStatus = raw.status
        ? (String(raw.status).toLowerCase() as DataDownloadRequest["status"])
        : raw.status;
      const inferredHasRequest = Boolean(
        raw.hasRequest || raw.status || raw.downloadUrl
      );
      setDownloadRequest({
        ...raw,
        status: normalizedStatus,
        hasRequest: inferredHasRequest,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setDataDownloadError(message || t("profile.data_download.backend_not_reachable"));
    } finally {
      setIsCheckingDownloadStatus(false);
    }
  };

  const handleRateApp = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      window.open('https://apps.apple.com/app/cvmatch', '_blank');
    } else if (userAgent.includes('android')) {
      window.open('https://play.google.com/store/apps/details?id=com.cvmatch', '_blank');
    } else {
      window.open('https://trustpilot.com/review/cvmatchswiss.ch', '_blank');
    }
  };

  const handleContactSupport = () => {
    const subject = 'Support Request - CVMatch';
    const body = `Bonjour,\n\nJ'ai besoin d'aide concernant CVMatch.\n\nDétails de mon compte:\n- Email: ${user?.email || 'Non disponible'}\n- ID utilisateur: ${user?.id || 'Non disponible'}\n- Date: ${new Date().toLocaleDateString('fr-FR')}\n\nDescription du problème:\n[Décrivez votre problème ici]\n\nCordialement,\n${user?.name || 'Utilisateur CVMatch'}`;
    const mailtoUrl = `mailto:support@cvmatchswiss.ch?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  const handleCreateDataDownloadRequest = async () => {
    if (!user?.id) return;
    setIsCreatingDownloadRequest(true);
    setDataDownloadError(null);
    try {
      const res = await dataDownloadService.createDownloadRequest(
        user.id,
        session as Session,
        user.email
      );
      setDataDownloadSuccess(res.message);
      // Optimistically reflect request presence
      setDownloadRequest({
        hasRequest: true,
        status: (String(res.status || 'pending').toLowerCase() as DataDownloadRequest['status']) || 'pending',
        apiStatus: undefined,
        downloadUrl: undefined,
        fileName: undefined,
        fileSize: undefined,
        expiresAt: undefined,
        createdAt: res.createdAt,
        updatedAt: undefined,
        message: undefined,
      });
      await loadDownloadStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setDataDownloadError(message || t("profile.data_download.backend_not_reachable"));
    } finally {
      setIsCreatingDownloadRequest(false);
    }
  };

  const handleDownloadDataZip = async () => {
    if (!downloadRequest?.downloadUrl || !downloadRequest?.fileName) return;
    setIsDownloadingData(true);
    setDataDownloadError(null);
    try {
      await dataDownloadService.downloadFile(
        downloadRequest.downloadUrl,
        downloadRequest.fileName,
        session as Session,
      );
      setDataDownloadSuccess(t("profile.data_download.download_started"));
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setDataDownloadError(message || t("profile.data_download.backend_not_reachable"));
    } finally {
      setIsDownloadingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id || !session?.access_token) return;
    
    setIsDeletingAccount(true);
    setDeleteAccountError(null);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/self-delete-user`, {}, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.data.isSuccess) {
        throw new Error(response.data.error || t("profile.delete_account.failed"));
      }

      // Account deleted successfully, sign out and redirect to welcome screen
      props.onClose();
      
      // Sign out the user completely
      await signOut();
      
      // Clear any cached data
      localStorage.clear();
      sessionStorage.clear();
      
      // Force reload to ensure complete cleanup and redirect to welcome screen
      // window.location.href = '/';
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "";
      setDeleteAccountError(message || t("profile.delete_account.failed"));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const renderInlineStatus = () => {
    const status = downloadRequest?.status
      ? String(downloadRequest.status).toLowerCase()
      : null;
    const statusLabel = t(
      `profile.data_download.status_${status || "unknown"}`
    );
    const statusClassMap: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      ready: "bg-green-100 text-green-800",
      expired: "bg-red-100 text-red-800",
      failed: "bg-red-100 text-red-800",
      unknown: "bg-gray-100 text-gray-800",
    };
    const cls = statusClassMap[status || "unknown"];
    const Icon =
      status === "pending"
        ? Clock
        : status === "processing"
        ? Loader2
        : status === "ready"
        ? CheckCircle
        : AlertCircle;

    return (
      <div className="w-full flex flex-col items-start gap-3">
        <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${cls}`}>
          <Icon className={`w-4 h-4 ${status === "processing" ? "animate-spin" : ""}`} />
          <span className="font-medium text-sm">{statusLabel}</span>
        </div>
        {status === "ready" && (
          <button
            onClick={handleDownloadDataZip}
            disabled={isDownloadingData}
            className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloadingData ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("profile.data_download.downloading")}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                {t("profile.data_download.download_now")}
              </>
            )}
          </button>
        )}
        <button
          onClick={loadDownloadStatus}
          className="w-full inline-flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors"
        >
          {t("profile.data_download.refresh_status")}
        </button>
      </div>
    );
  };


  useEffect(() => {
    if (dataDownloadError) {
      const timer = setTimeout(() => setDataDownloadError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [dataDownloadError]);

  useEffect(() => {
    if (dataDownloadSuccess) {
      const timer = setTimeout(() => setDataDownloadSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [dataDownloadSuccess]);

  useEffect(() => {
    if (deleteAccountError) {
      const timer = setTimeout(() => setDeleteAccountError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [deleteAccountError]);

  useEffect(() => {
    if (props.isOpen && user?.id) {
      loadDownloadStatus();
    }
  }, [props.isOpen, user?.id]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
          props.onClose();
      }
    };

    if (props.isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [props]);

  // Check for payment success/cancellation in URL parameters when modal opens
  

  // Cancellation Modal Component
  
  // console.log('SubscriptionModal render - showCancellationModal:', showCancellationModal);
  // console.log('SubscriptionModal render - showSuccessModal:', showSuccessModal);
  
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <SettingsIcon className="w-6 h-6 text-indigo-600 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('subscription.settings_title')}</h2>
                <p className="text-gray-600 mt-1">{t('settings.subtitle')}</p>
              </div>
          </div>
          <button
            onClick={props.onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

          <div className="flex h-[calc(90vh-120px)]">
            <div className="w-64 bg-gray-50 p-4 border-r border-gray-200 overflow-y-auto">
              <div className="space-y-2">
                <button
                  onClick={() => setActiveSection('general')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center ${activeSection === 'general' ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'}`}
                >
                  <User className="w-5 h-5 mr-3" />
                  {t('settings.tabs.general')}
                </button>
                <button
                  onClick={() => setActiveSection('data')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center ${activeSection === 'data' ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'}`}
                >
                  <Database className="w-5 h-5 mr-3" />
                  {t('settings.tabs.data')}
                </button>
                <button
                  onClick={() => setActiveSection('legal')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center ${activeSection === 'legal' ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'}`}
                >
                  <FileText className="w-5 h-5 mr-3" />
                  {t('settings.tabs.legal')}
                </button>
                <button
                  onClick={() => setActiveSection('security')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center ${activeSection === 'security' ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'}`}
                >
                  <Shield className="w-5 h-5 mr-3" />
                  {t('settings.tabs.security')}
                </button>
                <button
                  onClick={() => setActiveSection('app')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center ${activeSection === 'app' ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'}`}
                >
                  <Smartphone className="w-5 h-5 mr-3" />
                  {t('settings.tabs.app')}
                </button>
              </div>
            </div>

            <div className="flex-1 p-6 overflow-y-auto">
              {activeSection === 'general' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('settings.general.title')}</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{t('settings.general.change_password.title')}</h4>
                          <p className="text-sm text-gray-600">{t('settings.general.change_password.desc')}</p>
                        </div>
                        <button
                          onClick={() => setShowChangePasswordModal(true)}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                        >
                          {t('settings.general.change_password.button')}
                        </button>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{t('settings.general.rate_app.title')}</h4>
                          <p className="text-sm text-gray-600">{t('settings.general.rate_app.desc')}</p>
                        </div>
                        <button
                          onClick={handleRateApp}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center"
                        >
                          <Star className="w-4 h-4 mr-1" />
                          {t('settings.general.rate_app.button')}
                        </button>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{t('settings.general.support.title')}</h4>
                          <p className="text-sm text-gray-600">{t('settings.general.support.desc')}</p>
                        </div>
                        <button
                          onClick={handleContactSupport}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          {t('settings.general.support.button')}
                        </button>
                </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'data' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('settings.data.title')}</h3>
                  <div className="mt-2">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-blue-200">
                      <div className="flex flex-col sm:flex-row items-start justify-start sm:items-center gap-4 ">
                        <div className="flex-1 place-self-start">
                          <h4 className="font-semibold text-blue-900 mb-2">
                            {t("profile.data_download.title")}
                          </h4>
                          <p className="text-sm text-blue-800 mb-4 sm:mb-0">
                            {t("profile.data_download.description")}
                          </p>
                        </div>
                        <div className="w-full sm:w-auto">
                          {isCheckingDownloadStatus ? (
                            <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {t("profile.data_download.checking_status")}
                            </div>
                          ) : downloadRequest?.hasRequest && [
                              'pending',
                              'processing',
                              'ready',
                            ].includes(String(downloadRequest?.status || '').toLowerCase()) ? (
                            renderInlineStatus()
                          ) : (
                            <button
                              onClick={handleCreateDataDownloadRequest}
                              disabled={isCreatingDownloadRequest}
                              className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isCreatingDownloadRequest ? (
                                <>  
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  {t("profile.data_download.creating_request")}
                                </>
                              ) : (
                                <>
                                  <FileText className="w-4 h-4 mr-2" />
                                  {t("profile.data_download.button")}
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 flex flex-col gap-1">
                        <div className="">
                          <span className="text-xs text-blue-800 font-semibold mb-1">
                            {t("profile.data_download.info_time_title")}
                          </span>
                          <span className="text-xs text-blue-800 font-semibold mb-1"> : </span>
                          <span className="text-sm text-blue-600">
                            {t("profile.data_download.info_time_value")}
                          </span>
                        </div>
                        <div className="">
                          <span className="text-xs text-blue-800 font-semibold mb-1">
                            {t("profile.data_download.info_format_title")}
                          </span>
                          <span className="text-xs text-blue-800 font-semibold mb-1"> : </span>
                          <span className="text-sm text-blue-600">
                            {t("profile.data_download.info_format_value")}
                          </span>
                        </div>
                        <div className="">
                          <span className="text-xs text-blue-800 font-semibold mb-1">
                            {t("profile.data_download.info_expires_title")}
                          </span>
                          <span className="text-xs text-blue-800 font-semibold mb-1"> : </span>
                          <span className="text-sm text-blue-600">
                            {t("profile.data_download.info_expires_value")}
                          </span>
                        </div>
                      </div>
                      {dataDownloadError && (
                        <p className="text-red-600 bg-red-200 border border-red-300 rounded-lg p-2 text-sm mt-3">{dataDownloadError}</p>
                      )}
                      {dataDownloadSuccess && (
                        <p className="text-green-600 text-sm mt-3">{dataDownloadSuccess}</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4 border border-red-200 mt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-red-900 mb-2">{t('profile.delete_account.title')}</h4>
                        <p className="text-sm text-red-800 mb-2">{t('profile.delete_account.description')}</p>
                        <p className="text-xs text-red-700">⚠️ <strong>{t('profile.delete_account.warning_title')}</strong></p>
                      </div>
                      <button
                        onClick={() => setIsDeleteAccountModalOpen(true)}
                        disabled={isDeletingAccount}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {t('profile.delete_account.button')}
                      </button>
                    </div>
                    {deleteAccountError && (
                      <p className="text-red-600 bg-red-200 border border-red-300 rounded-lg p-2 text-sm mt-3">{deleteAccountError}</p>
                  )}
                </div>
              </div>
              )}

              {activeSection === 'legal' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('settings.legal.title')}</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{t('footer.terms_of_use')}</h4>
                          <p className="text-sm text-gray-600">{t('settings.legal.terms_desc')}</p>
                        </div>
                        <button onClick={() => window.open('/terms-and-conditions','_blank')} className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          {t('settings.view')}
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{t('footer.privacy_policy')}</h4>
                          <p className="text-sm text-gray-600">{t('settings.legal.privacy_desc')}</p>
                        </div>
                        <button onClick={() => window.open('/privacy-policy','_blank')} className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          {t('settings.view')}
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{t('footer.legal_info')}</h4>
                          <p className="text-sm text-gray-600">{t('settings.legal.legal_desc')}</p>
                        </div>
                        <button onClick={() => window.open('/legal-notice','_blank')} className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          {t('settings.view')}
                        </button>
                  </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'security' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('settings.security.title')}</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{t('settings.security.logout_title')}</h4>
                          <p className="text-sm text-gray-600">{t('settings.security.logout_desc')}</p>
                  </div>
                        <button
                          onClick={async () => { await signOut(); props.onClose(); }}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center"
                        >
                          <LogOut className="w-4 h-4 mr-1" />
                          {t('header.logout')}
                        </button>
                    </div>
                </div>

                    <div className="bg-gray-100 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-700">{t('settings.security.twofa_title')}</h4>
                          <p className="text-sm text-gray-500">{t('settings.security.twofa_desc')}</p>
                        </div>
                        <button disabled className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed">
                          {t('settings.security.coming_soon')}
                        </button>
                  </div>
                    </div>
                </div>
              </div>
              )}

              {activeSection === 'app' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('settings.app.title')}</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">{t('settings.app.website_version')}: 1.0.1</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">{t('settings.app.frontend')}</span>
                          <span className="font-medium ml-2">1.0.1</span>
                        </div>
                        <div>
                          <span className="text-gray-600">{t('settings.app.backend')}</span>
                          <span className="font-medium ml-2">1.0.1</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">{t('settings.app.linked_platforms')}</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">iOS</span>
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">{t('settings.app.active')}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Android</span>
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">{t('settings.app.active')}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Web</span>
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">{t('settings.app.active')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <p>💡 <strong>{t('settings.footer_tip_title')}</strong> {t('settings.footer_tip')}</p>
              </div>
              <button onClick={props.onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors">
                {t('profile.close')}
                  </button>
            </div>
        </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {isDeleteAccountModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-red-900">
                  {t("profile.delete_account.confirm_title")}
                </h3>
              </div>
              <p className="text-gray-700 mb-6">
                {t("profile.delete_account.confirm_description")}
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-red-800 mb-1">
                      {t("profile.delete_account.warning_title")}
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• {t("profile.delete_account.warning_1")}</li>
                      <li>• {t("profile.delete_account.warning_2")}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setIsDeleteAccountModalOpen(false)}
                  disabled={isDeletingAccount}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("profile.delete_account.cancel")}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeletingAccount}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("profile.delete_account.deleting")}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t("profile.delete_account.confirm_button")}
                    </>
                  )}
                </button>
          </div>
        </div>
        </div>
      </div>
      )}

      <ChangePasswordModal isOpen={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} />
    </>
  );
};

export default SubscriptionModal;